import React, { Component, useEffect } from 'react'
import styles from './Viewer.module.css'
import widgetConfigStyles from './WidgetConfiguration.module.css'
import { Button, Input, Label } from 'reactstrap'
import { FormGroup } from './FormGroup'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from './Dialog'
import axios, { isAxiosError } from 'axios'
import {Header} from './Header'
import Footer from './Footer'
import Notification from './Notification'
import Area from './Area'
import Spinner from './Spinner'
import {defaultViewerConfig} from './default-config'
// @ts-expect-error
import { Helmet } from 'react-helmet'
import { Dialog as InfoDialog, MenuItem, Select } from '@mui/material'
import { inflateLayout, LAYOUT_NAME, type LayoutName } from '../helpers/layout'
import { AutoHyperlink } from './AutoHyperlink'
import { FETCH_VIEWER_SUCCESS, SET_LAYOUT_NAME } from '../redux/project'
import { AppCtxProvider, HostedViewerCtx } from "../hooks/use-app-ctx"
import memoizeOne from "memoize-one"
import { BackendEventsVarPublisher } from '../api/backend-events'
import { getAndSetDefaultParameterValues } from './ViewerContextUtils'
import { FontListButton } from "./FontList"
import { SparqlTriggerExecutor } from './SparqlTriggerExecutor'
import { JsonEditor, JsonTextEditor } from "./JsonEditor"
import { format as formatDate } from "date-fns"
import "typeface-exo"
import "@fontsource-variable/exo-2"
import "@fontsource/inter"
import "@fontsource/inter/500.css"
import "@fontsource/inter/600.css"
import "@fontsource/inter/700.css"
import { AppVarsInUrlSyncer } from './AppVarsInUrlSyncer'
import { EventLogStoreProvider } from './editor/event-logs/event-log-store'
import { RuleExecutionLogsStoreProvider } from './RuleExecutionLogs/rule-execution-logs-store'
import { loadConfigLocal, saveConfigLocal } from '../model/local-mode'
import { LabelWithHelp } from './FormField'
import { getAuthHeaders, useWistorAuth, WistorAuth } from '../api/auth'
import { useDispatch, useSelector } from '../store'
import { type Dispatch } from 'redux'
import { RouteComponentProps } from 'react-router-dom'
import { Trigger, TriggerEditor, TriggersStartStopButtons } from './editor/trigger-editor'
import { WidgetConfig } from './WidgetConfiguration'
import { FeatureFlagValues, useFeatureFlags } from '../flags'
import { Alert, AlertDescription } from './ui-primitives/alert'
import { AppLoginState, getRepoName, useAppLoginStore } from '../api/app-login'
import { LocalizationProps } from './widgets/LocalizationUtils'
import { GLOBAL_NAMESPACE, ComponentNamespaceContext, resetAppVars, publish, publishDict } from '../redux/pubsub'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui-primitives/accordion'
import { TextArrayInput } from './ui-primitives/small/text-array-input'
import { TextTableInput } from './ui-primitives/small/text-table-input'
import { stableRefTo } from '../utils/react-utils'
import { initToolSyncBroadcaster } from '../helpers/tool-sync'
import { ResizablePanel, ResizablePanelGroup } from './ui-primitives/resizable'
import {
  Panel,
  PanelId,
  useHasPrimaryPanelVisible,
  useVisiblePanels,
} from './editor/panels/panel-registry'
import { Bug, Bolt, Activity, Braces, Settings, ListTree, Zap, MessageCircleWarning, UserRoundCog, Pause, Briefcase } from 'lucide-react'
import { AppVariables } from './editor/app-variables'
import { PanelSlot, PanelSlots } from './editor/panels/panel-slot'
import { SidebarRail } from './editor/panels/sidebar-rail'
import { EventLogs } from './editor/event-logs/event-logs'
import { WidgetTree, WidgetTreeHeaderIcons } from './editor/widget-tree'
import { WidgetSelector, WidgetSelectorHandle } from './editor/widget-selector/widget-selector'
import { QueryChips } from './editor/widget-selector/query-chips'
import { WidgetSettingsOverlay } from './editor/widget-selector/settings-overlay'
import { SettingsSizeIcons } from './editor/widget-editor/settings-size-icons'
import { ConfigurationTopBar } from './editor/configuration-top-bar/configuration-top-bar'
import { AppConfigurationTopBar } from './editor/ConfigurationTopBar'
import { WidgetEditorForm } from '@/components/editor/widget-editor/editor-form'
import { LocalSettings } from '@/components/editor/local-settings'
import { initializeViewerUndoRedo } from '@/components/editor/widget-editor/undo-redo'
import { ConfigurationMode, useConfigMode } from '@/components/editor/configuration-mode'
import { AppSettings } from '@/components/editor/app-settings'
import { PlatformTools } from '@/components/editor/platform-tools'
import { CodeWidgetCheckPanels } from '@/components/editor/code-widget-check-panels'
import { DebugRules, DebugRulesHeaderIcons, DebugRulesBadge } from '@/components/editor/debug-rules'
import { YasguiConsole } from '@/components/editor/yasqui-console'
import { Sparql } from '@/icons/sparql'
import { voteToDisableIframePointerEvents } from '@/helpers/toggle-iframe-events'

declare global {
  interface Window {
    PROJECT?: string // lock&publish
    VIEWER?: string // lock&publish
    CONFIG_URL?: string // lock&publish
  }
  interface Document {
    st?: () => void
    preview?: () => void
    edit?: () => void
  }
}

type State = {
  mode: 'edit' | 'preview'
  popupInfoVisible: boolean
  dialogVisible: boolean
  notification: null | {
    type: 'success' | 'failure'
    text: string
  }
  loading: boolean
  showJSONEditor: boolean
  config: {} | null
  notShowTop?: boolean
  viewer: ViewerConfig
  viewerOriginal: ViewerConfig
  update60Running?: boolean
  triggersPaused: boolean
}

export type Props
  = RouteComponentProps
  & {
    update60Running?: boolean // DEBT: this is not passed anywhere
    auth: WistorAuth
    dispatch: Dispatch
    project: any
    pubsub: any
    flags: FeatureFlagValues
    appLoginStore: AppLoginState
    visiblePanels: Set<PanelId>
    hasPrimaryPanelVisible: boolean
    configMode: ConfigurationMode
  }

export type ViewerConfig // DEBT: not sure yet how proper these types are
  = {
      id: string
      public?: boolean
      createdAt: string
      project: Project
      user: {}
      path: string
      new?: boolean
      deleted?: boolean
    }
  & {
      title: string
      subtitle: string
      infoMessage?: string
      bbsparqlendpoint: string
      repoURL: string
      localRepoURL?: string
      anonymousLogin?: boolean
      mainArea: WidgetConfig
      triggers?: Trigger[]
      sparqlErrorMessage?: string
      parameterValues?: string
      popupInfoAtStart?: boolean
      update60Enabled?: boolean
      sseEnabled?: boolean
      displayChromiumTranslateErrors?: boolean
      parseUrlProperties?: boolean
      prejs?: string
    }
  & {
      logoURL?: string
      logoutbutton?: boolean
      layoutName: "CUSTOM" | "ORIGINAL"
      notShowTop?: boolean
      notShowBottom?: boolean
      pageWidth?: string
      css: string
    }
  & {
      updateURLVariables?: string[]
      pushURLVariables?: string[]
    }
  & {
      refetchOnWindowFocus?: boolean
      refetchOnReconnect?: boolean
      strictSparqlErrors?: boolean
      notifySparqlErrors?: boolean
  }
  & LocalizationProps
  & AsComponentProps

type AsComponentProps = {
  componentInputVarNames?: string[]
  componentOutputVarNames?: string[]
  componentDebugDefaultVars?: Record<string, string>
}

type Project = {
  title: string,
  path: string,
  createdAt: string,
}

export class ViewerComponent extends Component<Props, State> {

  timer?: ReturnType<typeof setTimeout> // DEBT: name too generic, maybe feature should be fixed
  update60?: boolean // DEBT: something that was used only for icdd-viewer, implementation should at least be cleaned up
  loop60?: boolean // DEBT: see above
  loadViewerIntoStateOnlyOnce?: boolean
  savingPromise: Promise<void> | null = null
  private toolSyncTeardown?: () => void
  widgetSelectorRef = React.createRef<WidgetSelectorHandle>()

  state: State = { // DEBT: not sure yet how to deal with this initial state
    mode: 'preview',
    popupInfoVisible: false,
    dialogVisible: false,
    notification: null,
    loading: true,
    showJSONEditor: false,
    config: null,
    viewer: {} as ViewerConfig, // DEBT: whilst converting to typescript, not clear what state should be like to make everything coherent
    viewerOriginal: {} as ViewerConfig, // DEBT: whilst converting to typescript, not clear what state should be like to make everything coherent
    triggersPaused: false
  }

  private getViewerCtx = memoizeOne((
    mode: ConfigurationMode,
    viewProps: ViewProps,
    mainArea: ViewerConfig["mainArea"],
  ): HostedViewerCtx => ({
    host: "viewer",
    mode,
    viewProps,
    mainArea,
    updateMainArea: this.onChange,
    save: this.save,
    widgetSelectorRef: this.widgetSelectorRef,
  }))

  onJsonEditClick = () => {
    this.setState({ showJSONEditor: true })
  }

  constructRepoLoginPath(bbsparqlendpoint: string, repoUrl: string): string {
    const { viewerPath } = getViewProps(this.props)
    const repoLoginParams = new URLSearchParams(bbsparqlendpoint)
    repoLoginParams.set('repoUrl', repoUrl)
    repoLoginParams.set('viewerPath', viewerPath)

    return '/loginbb?id=' + encodeURIComponent(btoa('?' + repoLoginParams.toString()))
  }

  async componentDidMount() {
    this.toolSyncTeardown = initToolSyncBroadcaster()
    const { projectPath, viewerPath } = getViewProps(this.props)

    let viewer = this.props.project.viewers.find((v: any) => v.path === viewerPath)
    if (viewer && viewer.loaded) {
      console.log("component did mount load into state");
      await this.props.appLoginStore.connectToBackend(viewer, this)
      return this.loadViewerIntoState(viewer)
    }

    // no idea where CONFIG_URL comes from....
      if ((window.CONNECTED_APPS_STATIC) && (window.CONFIG_URL==null))
      {
        window.CONFIG_URL="./config.json";
      }

    const request = window.CONNECTED_APPS_STATIC
      ? {
          method: 'get',
          url: window.CONFIG_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      : {
          method: 'get',
          url: `/api/projects/${projectPath}/viewers/${viewerPath}`,
          headers: getAuthHeaders(),
        }

    let config: ViewerConfig
    try {
     // console.log(request);

    if (!this.props.flags.localMode) {
      const configResponse = await axios(request)
      config = configResponse.data
    } else {
      config = loadConfigLocal()
    }

    } catch(error) {
      if (!isAxiosError(error)) throw error
      if (error.response?.status === 401) return this.props.history.push('/login', `projects/${projectPath}/${viewerPath}`)
      if (error.response?.status === 404) return this.loadNewViewerConfig(viewerPath)
      throw error
    }
    if (!config) throw new Error('somehow no config was fetched')

    if (window.CONNECTED_APPS_STATIC == null && config.repoURL.startsWith('http') && !this.props.flags.localMode)
    {
      if (config.repoURL.includes("BloodBee"))
      {
            // (Hans: so annoying so skipping this when it contains BloodBee
      }
      else
      {
        alert(`Attention: This app interacts with external triplestore-endpoint "${config.repoURL}". Ignore this message if this is on purpose. This message will not show up when this app is locked-and-published.`)
      }

    }

    if (config.prejs != null) {
      try {
        eval(config.prejs) // eslint-disable-line no-eval
      } catch (error2) {
        console.log(error2, config.prejs)
      }
    }

    const loadConfig = async () => {
      //console.log("load config load into state");
      await this.loadViewerIntoState(config)
      this.props.dispatch({ type: FETCH_VIEWER_SUCCESS, data: config })
    }

    if (window.location.search.toLowerCase().includes("debug=skiplogin")) {
      console.log("debug skiplogin");
      loadConfig();return;
    }

    await this.props.appLoginStore.connectToBackend(config, this)

    loadConfig()
  }

  loadNewViewerConfig(viewerPath: string) {
    if (this.props.flags.localMode) return false
    const deflatedConfig = { ...defaultViewerConfig, path: viewerPath, new: true }
    initializeViewerUndoRedo({
      initialState: deflatedConfig as any,
      onStateUpdated: newViewer => this.loadExternallyChangedViewer(newViewer)
    })
    this.setState({
      viewer: inflateLayout(deflatedConfig as any) as ViewerConfig, // tiny DEBT: could not be bothered with typing it right anymore
      viewerOriginal: inflateLayout(deflatedConfig as any) as ViewerConfig, // tiny DEBT: could not be bothered with typing it right anymore
      loading: false,
    }, () => {
      this.save({ showNotification: false }) // Do not show 'saved' notification on creation of a new app
    })
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const viewer = this?.state?.viewer
    if (viewer == null) return

    if (viewer !== prevState.viewer) {
      this.props.dispatch({
        type: 'SET_VIEWER_CONFIG',
        data: viewer
      })
    }

    if (this.state.viewer.update60Enabled) {
      if (this.props.update60Running) {
      } else {
        if (this.update60) return;
        this.update60=true;
        var me =this;
        setTimeout(function () { me.changingProps()},500 );
      }
    } else {
      if (this.state.update60Running) {
        console.log("disabling update 60")
        publishDict({update60Running: false}, GLOBAL_NAMESPACE, 'disabling update 60')
      } else {
      }
    }
  }

  loadExternallyChangedViewer(viewer: ViewerConfig) {
    this.props.dispatch({
      type: "SET_VIEWER_CONFIG",
      data: viewer
    })
    this.setState({
      viewer: inflateLayout(viewer),
      viewerOriginal: inflateLayout(viewer),
      loading: false
    })
  }

  loadViewerIntoState(viewer: ViewerConfig) {
    if (this.loadViewerIntoStateOnlyOnce!=null) return;
    this.loadViewerIntoStateOnlyOnce=true;

    return new Promise<void>((resolve, reject) => {
      try {
        const deflatedConfig = inflateLayout(viewer)
        initializeViewerUndoRedo({
          initialState: deflatedConfig as any,
          onStateUpdated: newViewer => this.loadExternallyChangedViewer(newViewer)
        })
        this.setState({
          viewer: deflatedConfig,
          viewerOriginal: deflatedConfig,
          loading: false,
          popupInfoVisible: viewer.popupInfoAtStart ?? false,
        }, resolve)
        this.props.dispatch({ type: SET_LAYOUT_NAME, data: viewer.layoutName })
        this.registerAppCss(viewer.css)

        var r = getAndSetDefaultParameterValues(viewer);
        for (var key in r) {
        //  console.log("setting default parameters ",key,r[key])
          publish(key, r[key as keyof typeof r], GLOBAL_NAMESPACE, 'set default app vars (default parameter values)')
        }

        for (const [key, value] of Object.entries(viewer.componentDebugDefaultVars ?? {})) {
          publish(key, value, GLOBAL_NAMESPACE, 'set component default vars (only for debugging)')
        }
      } catch(e) {
        reject(e)
      }
    })
  }

  saveTriggers = async (triggers: Trigger[]) => {
    await this.updateViewer({ triggers })
    await this.save()
  }

  async updateViewer(partialViewer: Partial<ViewerConfig>) {
    return new Promise<void>(resolve => {
      this.setState({ viewer: { ...this.state.viewer, ...partialViewer } }, resolve)
    })
  }

  uuidv4() {
    // @ts-expect-error: DEBT: clean up when it matters more to use than implementing login.wistor.nl auth
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    )
  }
  changingProps() {
    if (this.state.update60Running) return
    if (this.props.update60Running) return
    // console.log("setting first update60 value");

    publishDict({update60: 'start', update60Running: true}, GLOBAL_NAMESPACE, 'start of update60 thing')
    this.changingProps60(true)
  }

  changingProps60(b: boolean) {
    if (b || this.state.viewer.update60Enabled) {
      var me = this
      if (me.loop60) return
      me.loop60 = true
      setTimeout(function () {
         console.log("changing update60 value ");
        let id = me.uuidv4()
        publishDict({update60: id}, GLOBAL_NAMESPACE, 'changing update60 value ')
       // console.log('changing 60')
        me.loop60 = false
        me.changingProps60(false)
      }, 60_000)
    }
  }

  componentWillUnmount() {
    this.toolSyncTeardown?.()
    resetAppVars()
    clearTimeout(this.timer)
  }

  onChange = (config: ViewerConfig['mainArea']) => {
    let viewer = { ...this.state.viewer, mainArea: config }
    this.setState({ viewer })
    return viewer
  }

  selectLayout = (layoutName: LayoutName) => {
    let viewer = inflateLayout({ ...this.state.viewer, layoutName })
    this.setState({ viewer })
    this.props.dispatch({ type: SET_LAYOUT_NAME, data: layoutName })
    return viewer
  }

  save = async (opts?: {showNotification: false}) => {
    if (this.savingPromise) {
      try {
        await this.savingPromise
      } catch (e) {
        // ignore
      }
    }

    this.registerAppCss(this.state?.viewer?.css)
    this.savingPromise = this.saveImpl(opts)
    return this.savingPromise
  }

  saveImpl = async ({showNotification = true} = {}) => {
    if (this.props.flags.localMode) return saveConfigLocal(this.state.viewer)

    let viewer: Partial<ViewerConfig> = this.state.viewer
    delete viewer.project

    const { projectPath, viewerPath } = getViewProps(this.props)

    try {
      const response = await axios({
        method: viewer.new ? 'post' : 'put',
        url: `/api/projects/${projectPath}/viewers` + (viewer.new ? '' : `/${viewerPath}`),
        headers: getAuthHeaders(),
        data: viewer,
      })
      const { data } = response

      if (showNotification) {
        if (this.timer) clearTimeout(this.timer)
        this.setState({ notification: { text: 'saved', type: 'success' } })
        this.timer = setTimeout(() => this.setState({ notification: null }), 3000)
      }

      const inflatedViewer = inflateLayout({ ...data, new: false })
      initializeViewerUndoRedo({
        initialState: inflatedViewer as any,
        onStateUpdated: newViewer => this.loadExternallyChangedViewer(newViewer)
      })
      await this.setStatePromise({
        viewer: inflatedViewer,
        viewerOriginal: inflatedViewer
      })
      const {project, ...strippedData} = data
      this.props.dispatch({ type: 'SAVE_VIEWER_SUCCESS', data: strippedData })

    } catch(error) {

      if (!isAxiosError(error)) return
      if (error.response?.status === 401) {
        this.props.history.push('/login', `projects/${projectPath}/${viewerPath}`)
      } else {
        this.setState({
          notification: {
            text: 'not saved: ' + error.response?.data,
            type: 'failure',
          },
        })
        this.timer = setTimeout(() => this.setState({ notification: null }), 3000)
      }

    } finally {
      this.savingPromise = null
    }
  }

  setStatePromise<K extends keyof State>(
    setter: Pick<State, K> | ((prevState: Readonly<State>, props: Readonly<Props>) => Pick<State, K>)
  ): Promise<void> {
    return new Promise(resolve => {
      this.setState(setter, () => resolve())
    })
  }

  /** @param [appCssJson] appCss JSON-string of something that should be Record<string, string | number> */
  registerAppCss (appCssJson: string) {
    if (!appCssJson) return this.props.dispatch({type: 'SET_APP_CSS'})
    try {
      const appCss = JSON.parse(appCssJson)
      this.props.dispatch({type: 'SET_APP_CSS', data: appCss})
    } catch(e) {
      console.error(e)
    }
  }

  onDialogCancelClick = () => {
    this.setState({viewer: this.state.viewerOriginal})
    this.setState({ dialogVisible: false })
  }

  onDialogOkClick = () => {
    void this.save()
    this.setState({ dialogVisible: false })
  }

  jsonOKClick = () => {
    this.setState({ showJSONEditor: false })
  }

  voteToDisableUserActions = (voterId: string, userActionsDisabled: boolean) => {
    this.props.dispatch({
      type: "VOTE_USER_ACTIONS_DISABLED",
      data: { voterId, value: userActionsDisabled }
    })
  }

  render() {
    if (this == null) return <Spinner /> // DEBT: this should not be possible lol
    if (this.state == null) return <Spinner /> // DEBT: this should not be possible lol
    if (this.state.loading) return <Spinner />

    const { mode, projectPath, viewerPath } = getViewProps(this.props)

    const viewer = this.state.viewer
    if (!viewer || viewer.deleted) return (
      <p>This viewer does not exist or has been deleted. Please contact an admin if this is a problem.</p>
    )

    document.st = () => this.setState({ notShowTop: false })
    document.preview = () => this.setState({ mode: 'preview' })
    document.edit = () => this.setState({ mode: 'edit' })

    let notification = this.state.notification

    const showTop = !viewer.notShowTop

    let css = {}
    if (viewer.css) {
      try {
        css = JSON.parse(viewer.css)
      } catch (error) {
        console.error(error)
      }
    }

    const repoName = getRepoName(viewer.bbsparqlendpoint)
    const isExperimentalUx = this.props.flags.experimentalConfiguratorUx
    const showDebugPanels = isExperimentalUx && (this.props.configMode === "debug" || this.props.configMode === "edit")
    const showEditPanels = isExperimentalUx && this.props.configMode === "edit"
    const showPanels = isExperimentalUx && this.props.configMode !== "view"

    const wistorAppRoot = (
      <div id="wistor-app-root" className="relative flex flex-col flex-grow overflow-auto">
        {showTop && (
          <Header
            title={viewer.title}
            subtitle={viewer.subtitle}
            repoURL={viewer.repoURL}
            localRepoURL={viewer.localRepoURL}
            bbsparqlendpoint={viewer.bbsparqlendpoint}
            pageWidth={viewer.pageWidth}
            parseUrlProperties={viewer.parseUrlProperties}
            logoutbutton={viewer.logoutbutton}
            noLoginButton
            logoURL={viewer.logoURL}
            onInfoClick={viewer.infoMessage ? () => this.setState({ popupInfoVisible: true }) : undefined}
          />
        )}

        <div className={styles.main}
             style={{ maxWidth: viewer.pageWidth, minWidth: '66vw' /* for edit mode/config? */ }}>
          <div className={styles.areaContainer}>
            <Area
              config={viewer.mainArea}
              onChange={this.onChange}
              mode={isExperimentalUx ? "preview" : mode}
              repoName={repoName}
              repoURL={viewer.repoURL}
              localRepoURL={viewer.localRepoURL}
              parseUrlProperties={viewer.parseUrlProperties}
              sparqlErrorMessage={viewer.sparqlErrorMessage}
              parameterValues={viewer.parameterValues}
              save={this.save}
              pubsub={this.props.pubsub}
              namespace={GLOBAL_NAMESPACE}
              configMode={this.props.configMode}
            />
          </div>
        </div>

        {(this.state.viewer as any)?.layout?.showFooter && ( // DEBT: see layout.ts
          <Footer />
        )}

      </div>
    )

    const panelStyles = {
      style: {
        // show over bbpopup MUI modals
        position: 'relative',
        zIndex: 2000,
      },
    } as const

    const triggersToExecute = this.state.triggersPaused
      ? []
      : (viewer.triggers ?? [])

    return (
      <EventLogStoreProvider>
        <ComponentNamespaceContext.Provider value={GLOBAL_NAMESPACE}>
        <RuleExecutionLogsStoreProvider>
        <AppCtxProvider
          repoURL={viewer.repoURL}
          localRepoURL={viewer.localRepoURL}
          bbsparqlendpoint={viewer.bbsparqlendpoint}
          displayChromiumTranslateErrors={viewer.displayChromiumTranslateErrors}
          viewerCtx={this.getViewerCtx(
            this.props.configMode,
            getStableViewProps(this.props),
            viewer.mainArea,
          )}
        >
          <BackendEventsVarPublisher />
          <div className={styles.wrapper} style={css}>
            <AppVarsInUrlSyncer varsToReplace={viewer.updateURLVariables} varsToPush={viewer.pushURLVariables} />
            {triggersToExecute.map(trigger => (
              <SparqlTriggerExecutor
                signalIsFetching={(isFetching) => {
                  if (trigger.voteForUserActionsDisabledWhenLoading)
                    this.voteToDisableUserActions(trigger.label, isFetching)
                }}
                key={trigger.label}
                templateId={trigger.label}
                sparqlTemplate={trigger.sparqlQuery}
              />
            ))}
            <div className={styles.mainContainer}>
              <Helmet>
                <title>{viewer.title}</title>
                {viewer.project && (
                  <link
                    rel="icon"
                    type="image/png"
                    href={`${window.CONNECTED_APPS_STATIC ? '.' : ''}/favicon-${viewer.project.path}.png`}
                    sizes="16x16"
                  />
                )}
              </Helmet>
              {notification && <Notification messageType={notification.type} text={notification.text} />}

              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {!window.CONNECTED_APPS_STATIC /* todo weird name? */ && (
                  isExperimentalUx ? (
                    <ConfigurationTopBar />
                  ) : (
                    <AppConfigurationTopBar
                      projectPath={projectPath}
                      viewerPath={viewerPath}
                      repoURL={viewer.repoURL}
                      repoName={repoName ?? ''}
                      localRepoURL={viewer.localRepoURL}
                      mode={mode}
                      openAppSettings={() => this.setState({ dialogVisible: true })}
                      saveTriggers={this.saveTriggers}
                      triggers={viewer.triggers ?? []}
                    />
                  )
                )}
                {showPanels && (/*claude: can we move the panels to a new file?*/
                  <>
                    {showEditPanels && (
                      <>
                        <Panel
                          id="widget-edit"
                          role="primary"
                          title="Edit widgets"
                          sortOrder={10}
                          icon={Bolt}
                          renderHeaderExtraIcons={() => <SettingsSizeIcons />}
                          defaultFloatingDimensions={{
                            width: 600,
                            height: 800,
                          }}
                        >
                          <WidgetEditorForm />
                        </Panel>
                        <Panel
                          id="widget-tree"
                          role="primary"
                          title="Widget tree"
                          icon={ListTree}
                          sortOrder={20}
                          renderHeaderExtraIcons={() => <WidgetTreeHeaderIcons />}
                        >
                          <WidgetTree />
                        </Panel>
                        <Panel
                          id="app-settings"
                          role="primary"
                          title="App settings"
                          icon={Settings}
                          sortOrder={30}
                          defaultFloatingDimensions={{
                            width: 600,
                            height: 800,
                          }}
                        >
                          <AppSettings
                            viewerConfig={this.state.viewer}
                            onChange={(viewer) => this.setState({ viewer })}
                            selectLayout={(layout) => this.selectLayout(layout)}
                          />
                        </Panel>
                      </>
                    )}
                    {showDebugPanels && (
                      <>
                        <Panel
                          id="app-variables"
                          role="primary"
                          title="App variables"
                          icon={Braces}
                          sortOrder={40}
                          defaultFloatingDimensions={{
                            width: 600,
                            height: 600,
                          }}>
                          <AppVariables />
                        </Panel>
                        <Panel
                          id="triggers"
                          role="primary"
                          title={this.state.triggersPaused ? "Triggers (paused)" : "Triggers"}
                          icon={Zap}
                          sortOrder={41}
                          renderHeaderExtraIcons={() => (
                            <TriggersStartStopButtons
                              paused={this.state.triggersPaused}
                              togglePaused={() => this.setState(s => ({ triggersPaused: !s.triggersPaused }))}
                            />
                          )}
                          renderBadge={
                            this.state.triggersPaused
                              ? (
                                <Pause size={9}
                                       className="position-absolute"
                                       style={{ top: "1px", right: "1px" }}
                                       stroke="#b05152"
                                       fill="#b05152" />
                              ) : null
                          }
                          defaultFloatingDimensions={{
                            width: 1000,
                            height: 600,
                          }}>
                          <TriggerEditor
                            viewerConfig={this.state.viewer}
                            onChange={(viewer) => this.setState({ viewer })} />
                        </Panel>
                        <Panel
                          id="event-logs"
                          role="primary"
                          title="Event logs"
                          railGroup="debug"
                          icon={Activity}
                          sortOrder={50}
                          defaultFloatingDimensions={{
                            width: 1000,
                            height: 600,
                          }}>
                          <EventLogs />
                        </Panel>
                        <Panel
                          id="debug-rules"
                          role="primary"
                          title="Debug rules"
                          railGroup="debug"
                          icon={Bug}
                          sortOrder={60}
                          defaultFloatingDimensions={{
                            width: 1000,
                            height: 600,
                          }}
                          renderHeaderExtraIcons={() => <DebugRulesHeaderIcons />}
                          renderBadge={<DebugRulesBadge />}
                        >
                          <DebugRules />
                        </Panel>
                        {/* I WANT THIS, but not implemented yet */}
                        {/*<Panel*/}
                        {/*  id="messages"*/}
                        {/*  role="primary"*/}
                        {/*  title="App problems"*/}
                        {/*  icon={MessageCircleWarning}*/}
                        {/*  sortOrder={70}*/}
                        {/*  defaultFloatingDimensions={{*/}
                        {/*    width: 1000,*/}
                        {/*    height: 600,*/}
                        {/*  }}*/}
                        {/*>*/}
                        {/*  <div>app problems</div>*/}
                        {/*</Panel>*/}
                      </>
                    )}
                    <CodeWidgetCheckPanels />
                    <Panel
                      id="yasgui"
                      role="primary"
                      title="SPARQL query console"
                      icon={Sparql}
                      sortOrder={1000}
                      position="bottom"
                      defaultFloatingDimensions={{
                        width: 1000,
                        height: 600,
                      }}
                    >
                      <YasguiConsole />
                    </Panel>
                    <Panel
                      id="platform-tools"
                      role="primary"
                      title="Platform tools"
                      icon={Briefcase}
                      sortOrder={1002}
                      position="bottom"
                      defaultFloatingDimensions={{
                        width: 400,
                        height: 300,
                      }}
                    >
                      <PlatformTools
                        repoURL={this.state.viewer.repoURL}
                        localRepoURL={this.state.viewer.localRepoURL}
                      />
                    </Panel>
                    <Panel
                      id="local-settings"
                      role="primary"
                      title="Local settings"
                      icon={UserRoundCog}
                      sortOrder={1001}
                      position="bottom"
                      defaultFloatingDimensions={{
                        width: 1000,
                        height: 600,
                      }}
                    >
                      <LocalSettings />
                    </Panel>
                  </>
                )}
                {showPanels ? (
                  <ResizablePanelGroup orientation="horizontal"
                                       className="min-h-0 h-full overflow-hidden">
                    <ResizablePanel id="main" className="flex flex-grow">
                      {wistorAppRoot}
                    </ResizablePanel>

                    {(this.props.visiblePanels.has('event-logs-aux') || this.props.visiblePanels.has('debug-rules-aux')) && showDebugPanels && (
                      <ResizablePanel id="aux"
                                      minSize="150px"
                                      defaultSize="300px"
                                      {...panelStyles}>
                        <PanelSlot role="auxiliary" id="event-logs-aux" />
                        <PanelSlot role="auxiliary" id="debug-rules-aux" />
                      </ResizablePanel>
                    )}

                    {this.props.hasPrimaryPanelVisible && (
                      <ResizablePanel id="primary"
                                      minSize="150px"
                                      defaultSize="450px"
                                      {...panelStyles}>
                        <PanelSlots role="primary" />
                      </ResizablePanel>
                    )}

                    <ResizablePanel id="sidebar"
                                    defaultSize={40}
                                    minSize={40}
                                    maxSize={40}
                                    {...panelStyles}>
                      <SidebarRail />
                    </ResizablePanel>
                  </ResizablePanelGroup>
                ) : (
                  wistorAppRoot
                )}

                {showPanels && <PanelSlots role="any" floating />}
              </div>

              <InfoDialog
                style={{ zIndex: 1337 }}
                maxWidth="xl"
                open={Boolean(this.state.popupInfoVisible && viewer.infoMessage)}
                onClose={() => this.setState({ popupInfoVisible: false })}
              >
                <div className={styles.infoDialogueContainer}>
                  <AutoHyperlink text={viewer.infoMessage} />
                </div>
              </InfoDialog>

              {this.state.dialogVisible && (
                <Dialog onClose={this.onDialogCancelClick}>
                  <DialogHeader title="Wistor App Settings" />
                  <DialogContent>
                    <FormGroup>
                      <Label>Layout</Label>
                      <Select
                        className="dark-form-control form-control white-font-override"
                        onChange={(e) => this.selectLayout(e.target.value as any)} // `as any` is fine, as long as options below match
                        value={this.state.viewer.layoutName || ''}
                      >
                        <MenuItem value={LAYOUT_NAME.CUSTOM}>Custom</MenuItem>
                        <MenuItem value={LAYOUT_NAME.ORIGINAL}>Original</MenuItem>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Title</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.title || ''}
                        type="text"
                        onChange={(e) => this.updateViewer({ title: e.target.value })}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Subtitle</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.subtitle || ''}
                        type="text"
                        onChange={(e) => this.updateViewer({ subtitle: e.target.value })}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Info message</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.infoMessage || ''}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, infoMessage: e.target.value },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.popupInfoAtStart ?? false}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                popupInfoAtStart: e.target.checked,
                              },
                            })
                          }
                        />
                        show popup info at start
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>pre app javascript</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.prejs || ''}
                        // @ts-expect-error: DEBT: not sure if this actually does something
                        type="javascript"
                        onChange={(e) => {
                          this.setState({
                            viewer: { ...viewer, prejs: e.target.value },
                          })
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>bb repo and app parameters</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.bbsparqlendpoint || ''}
                        type="text"
                        onChange={(e) => {
                          this.setState({
                            viewer: { ...viewer, bbsparqlendpoint: e.target.value },
                          })
                        }}
                      />
                      {/[&?]login=.+[&]psw=.+|[&?]psw=.+[&]login=.+/i.test(viewer.bbsparqlendpoint || '') && (
                        <Alert variant="info" className="mt-2">
                          <AlertDescription>
                            ⚠️️ Deprecated auto-login method detected (&login=xxx&psw=xxx). Please configure an
                            anonymous app user instead. See{' '}
                            <a href="https://bim-connected.slite.com/app/docs/Wbh6CtS-ClmZDh#YkmqXuN5onfWDw">
                              this guide
                            </a>
                            .
                          </AlertDescription>
                        </Alert>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label>Sparql endpoint</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.repoURL || ''}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, repoURL: e.target.value },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.anonymousLogin ?? false}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) => {
                            this.setState({
                              viewer: {
                                ...viewer,
                                anonymousLogin: e.target.checked,
                              },
                            })
                          }}
                        />
                        Enable anonymous login for this app. An anonymous user must be set up in User Management for
                        this to work.
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>local sparql endpoint (http://localhost:7200/repositories/test)</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.localRepoURL}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, localRepoURL: e.target.value },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>sparql endpoint error message</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.sparqlErrorMessage || ''}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              sparqlErrorMessage: e.target.value,
                            },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Logo URL</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.logoURL || ''}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, logoURL: e.target.value },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label style={{ display: 'flex', gap: '1rem' }}>
                        <div style={{ flexGrow: 1 }}>
                          Global CSS in JSON formaat, bijvoorbeeld: {'{"fontFamily": "Exo"}'}
                        </div>
                        <FontListButton />
                      </Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.css || ''}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, css: e.target.value },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Pagina breedte</Label>
                      <Input
                        className="dark-form-control"
                        value={viewer.pageWidth || ''}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, pageWidth: e.target.value },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>default parameter values </Label>
                      <JsonTextEditor
                        value={viewer.parameterValues || ''}
                        onValueChange={(value) => {
                          this.setState({
                            viewer: { ...viewer, parameterValues: value },
                          })
                        }}
                      />
                    </FormGroup>

                  <FormGroup className="border !border-neutral-500 rounded">
                    <Accordion className="" type="single" collapsible>
                      <AccordionItem className="border-0" value="component">
                        <AccordionTrigger className="px-2 pl-3 py-1 text-xl rounded-none">as component</AccordionTrigger>
                        <AccordionContent className="px-4 py-0 pt-4">
                          <FormGroup>
                            <Label>input variables</Label>
                            <TextArrayInput variant="bottom-add-button"
                              value={viewer.componentInputVarNames ?? []}
                              onChange={value => this.setState(s => ({viewer: {
                                ...s.viewer, componentInputVarNames: value
                              }}))}
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>output variables</Label>
                            <TextArrayInput variant="bottom-add-button"
                              value={viewer.componentOutputVarNames ?? []}
                              onChange={value => this.setState(s => ({viewer: {
                                ...s.viewer, componentOutputVarNames: value
                              }}))}
                            />
                          </FormGroup>
                          <FormGroup className="mb-0">
                            <Label>default app-variables when not used as component</Label>
                            <TextTableInput
                              columnLabels={stableRefTo(['name', 'value'], 'app-as-component-debug-default-vars')}
                              valueOnlyAsInitial value={
                                Object.keys(viewer.componentDebugDefaultVars ?? {}).length
                                ? Object.entries(viewer.componentDebugDefaultVars as Record<string, string>)
                                : Array.from({length: 3}).map(() => ['', ''])
                              }
                              onChange={rows => this.setState(s => {
                                const vars: ViewerConfig['componentDebugDefaultVars'] = {}
                                for (const [key, value] of rows) {
                                  if (!key) continue
                                  vars[key] = value
                                }
                                return {viewer: {...s.viewer, componentDebugDefaultVars: vars}}
                              })}
                            />
                          </FormGroup>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.update60Enabled}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                update60Enabled: e.target.checked,
                              },
                            })
                          }
                        />
                        enable update60 parameter refresh at 60s interval
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.parseUrlProperties}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                parseUrlProperties: e.target.checked,
                              },
                            })
                          }
                        />
                        parse url parameters as property values
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.notShowTop}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: { ...viewer, notShowTop: e.target.checked },
                            })
                          }
                        />{' '}
                        do not show top
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.logoutbutton}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                logoutbutton: e.target.checked,
                              },
                            })
                          }
                        />
                        show logout button in the header
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.notShowBottom}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                notShowBottom: e.target.checked,
                              },
                            })
                          }
                        />
                        do not show bottom
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <LabelWithHelp
                        label={
                          <>
                            App-variables to encode in the URL as an <b>update</b> in the browser's history
                          </>
                        }
                        helpComponent={InfoUpdateURLAppVars}
                      />
                      <Input
                        className="dark-form-control"
                        value={(viewer.updateURLVariables || []).join(', ')}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              updateURLVariables: e.target.value.split(',').map((str) => str.trim()),
                            },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <LabelWithHelp
                        label={
                          <>
                            App-variables to encode in the URL as a <b>push</b> in the browser's history
                          </>
                        }
                        helpComponent={InfoPushURLAppVars}
                      />
                      <Input
                        className="dark-form-control"
                        value={(viewer.pushURLVariables || []).join(', ')}
                        type="text"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, pushURLVariables: e.target.value.split(',').map((str) => str.trim()) },
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.refetchOnWindowFocus}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                refetchOnWindowFocus: e.target.checked,
                              },
                            })
                          }
                        />
                        refresh SPARQL when returning to this tab or window
                      </Label>
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.refetchOnReconnect}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: {
                                ...viewer,
                                refetchOnReconnect: e.target.checked,
                              },
                            })
                          }
                        />
                        refresh SPARQL when the network connection is restored
                      </Label>
                    </FormGroup>

                    <FormGroup tag="fieldset" className={widgetConfigStyles.fieldset}>
                      <legend>On a failing SPARQL query</legend>
                      <FormGroup>
                        <Label>
                          <Input
                            checked={viewer.strictSparqlErrors ?? false}
                            className={styles.settingsCheckbox}
                            type="checkbox"
                            onChange={(e) =>
                              this.setState({
                                viewer: {
                                  ...viewer,
                                  strictSparqlErrors: e.target.checked,
                                },
                              })
                            }
                          />
                          replace the widget with the SPARQL error message
                        </Label>
                      </FormGroup>
                      <FormGroup>
                        <Label>
                          <Input
                            checked={viewer.notifySparqlErrors ?? false}
                            className={styles.settingsCheckbox}
                            type="checkbox"
                            onChange={(e) =>
                              this.setState({
                                viewer: {
                                  ...viewer,
                                  notifySparqlErrors: e.target.checked,
                                },
                              })
                            }
                          />
                          show a notification
                        </Label>
                      </FormGroup>
                    </FormGroup>

                    <FormGroup>
                    <Label>
                      <Input
                          checked={viewer.localizeDateTime ?? false}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) => {
                            this.setState({
                              viewer: {
                                ...viewer,
                                localizeDateTime: e.target.checked,
                              },
                            })
                          }}
                      />
                      Use Date localization to show dateTimes in local timezone (only implemented for table++)
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <LabelWithHelp
                        label={<>Provide the dateTime format you would like to use.</>}
                        helpComponent={InfoDateTimeLocalizationFormat}
                    />
                    <Input
                        className="dark-form-control"
                        value={viewer.dateTimeLocalizationFormat ?? "yyyy-MM-dd HH:mm"}
                        type="text"
                        onChange={(e) =>
                            this.setState({
                              viewer: { ...viewer, dateTimeLocalizationFormat: e.target.value },
                            })
                        }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                          checked={viewer.useCustomBooleanLables ?? false}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) => {
                            this.setState({
                              viewer: {
                                ...viewer,
                                useCustomBooleanLables: e.target.checked,
                              },
                            })
                          }}
                      />
                      Use custom presentation for Booleans
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <LabelWithHelp
                        label={<>Provide a comma seperated list that defines how you want booleans to appear.</>}
                        helpComponent={InfoBooleanLabelMapping}
                    />
                    <Input
                        className="dark-form-control"
                        value={(viewer.booleanLabelMapping || []).join(", ")}
                        type="text"
                        onChange={(e) =>
                            this.setState({
                              viewer: { ...viewer, booleanLabelMapping: e.target.value.split(",").map(str => str.trim()) },
                            })
                        }
                    />
                  </FormGroup>

                  <FormGroup>
                      <Label>Config in JSON formaat</Label>
                      <JsonEditor
                        exportFileName={`${this.state.viewer?.project?.path}-${
                          this.state.viewer?.path
                        }-config.${formatDate(new Date(), 'yyyy-MM-dd.HHmm')}.json`}
                        value={viewer}
                        onValueChange={(config) => {
                          if (config == null) return
                          this.setState(
                            (prevState) =>
                              ({
                                viewer: {
                                  ...config,
                                  id: prevState.viewer.id,
                                  user: prevState.viewer.user,
                                  path: prevState.viewer.path,
                                  project: prevState.viewer.project,
                                  createdAt: prevState.viewer.createdAt,
                                },
                              } as any)
                          ) // DEBT: this could introduce a wrongly typed viewer
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>
                        <Input
                          checked={viewer.public}
                          className={styles.settingsCheckbox}
                          type="checkbox"
                          onChange={(e) =>
                            this.setState({
                              viewer: { ...viewer, public: e.target.checked },
                            })
                          }
                        />
                        publiek
                      </Label>
                    </FormGroup>
                  </DialogContent>
                  <DialogFooter>
                    <Button color="secondary" style={{ minWidth: '120px' }} onClick={this.onDialogCancelClick}>
                      Cancel
                    </Button>
                    <Button color="primary" style={{ minWidth: '120px' }} onClick={this.onDialogOkClick}>
                      OK
                    </Button>
                  </DialogFooter>
                </Dialog>
              )}
            </div>
          </div>
          {isExperimentalUx && (
            <>
              <WidgetSelector ref={this.widgetSelectorRef} />
              <QueryChips />
              <WidgetSettingsOverlay />
            </>
          )}
        </AppCtxProvider>
      </RuleExecutionLogsStoreProvider>
      </ComponentNamespaceContext.Provider>
      </EventLogStoreProvider>
    )
  }
}

export default function Viewer(props: RouteComponentProps) {
  const auth = useWistorAuth()
  const dispatch = useDispatch()
  const project = useSelector(s => s.project)
  const pubsub = useSelector(s => s.rootAppVars_INTERNAL ?? {})
  const flags = useFeatureFlags()
  const appLoginStore = useAppLoginStore()
  const hasPrimaryPanelVisible = useHasPrimaryPanelVisible()
  const visiblePanels = useVisiblePanels()
  const configMode = useConfigMode()
  useWorkaroundToAllowResizingWithNearbyIframes(flags.experimentalConfiguratorUx && configMode !== "view")

  return (
    <ViewerComponent
      {...props}
      auth={auth}
      dispatch={dispatch}
      project={project}
      pubsub={pubsub}
      flags={flags}
      appLoginStore={appLoginStore}
      visiblePanels={visiblePanels}
      hasPrimaryPanelVisible={hasPrimaryPanelVisible}
      configMode={configMode}
    />
  )
}

export function shouldConnectToWistorBackend(
  config: Pick<ViewerConfig, 'localRepoURL' | 'deleted' | 'bbsparqlendpoint'>
) {
  const isConnectedToLocalRepo = config.localRepoURL?.includes(':7200') && window.location.host === 'localhost:3000'
  if (isConnectedToLocalRepo) return false
  if (config.deleted) return false
  if (window.location.search.includes("debug=skiplogin")) return false
  return Boolean(config.bbsparqlendpoint?.includes?.('repo='))
}

export type ViewProps = {
  mode: 'edit' | 'preview'
  projectPath: string
  viewerPath: string
}

export function getViewProps(props: Props): ViewProps {
  if (props.flags.localMode) {
    return {
      mode: (props.match.params as any).mode as 'edit' | 'preview',
      projectPath: '',
      viewerPath: '',
    }
  }
  if (window.CONNECTED_APPS_STATIC)
    return {
      mode: 'preview' as const,
      projectPath: window.PROJECT as string,
      viewerPath: window.VIEWER as string,
    }

  return {
    mode: (props.match.params as any).mode as 'edit' | 'preview',
    projectPath: (props.match.params as any).projectPath as string,
    viewerPath: (props.match.params as any).viewerPath as string,
  }
}

const stabilizeViewProps = memoizeOne((
  mode: ViewProps["mode"],
  projectPath: string,
  viewerPath: string
): ViewProps => {
  return { mode, projectPath, viewerPath }
})

export function getStableViewProps(props: Props): ViewProps {
  const { mode, projectPath, viewerPath } = getViewProps(props)
  return stabilizeViewProps(mode, projectPath, viewerPath)
}

function InfoUpdateURLAppVars() {
  return <>
    Changes in app-variables that are configured here will overwrite the current browser location.
    <i>The browser will be unable to go back in history to the previous set of app-variables.</i>
  </>
}

function InfoPushURLAppVars() {
  return <>
    Changes in app-variables that are configured here will push the current browser location.
    <i>The browser will be able to go back in history to the previous set of app-variables.</i>
  </>
}

function InfoBooleanLabelMapping() {
  return <>
    Provide a comma seperated list that define how you want booleans to appear,
    eg: "trueText, falseText, emptyText".<br/>
    <i>It will <b>only</b> work if 2 or 3 values are provided.</i><br/>
    If 2 values are given, meaning no value if given for emptyText, then emptyText will default to an empty string<br/>
    Note that the emptyText label is only used in places where the value is set, eg: BBDForm,
    in places where it is viewed such as table++ it will show up as empty
  </>
}

function InfoDateTimeLocalizationFormat() {
  return <>
    Provide the dateTime format you would like to use. It uses date-fns formatting tokens<br/>
    Examples:<br/>
    yyyy-MM-dd HH:mm → 2024-01-01 14:30<br/>
    dd-MM-yyyy → 01-01-2024<br/>
    HH:mm:ss → 14:30:45<br/>
    EEEE, dd MMMM yyyy → Monday, 01 January 2024<br/>
    yyyy-MM-dd'T'HH:mm:ss.SSSXXX → 2024-01-01T12:00:00.000+00:00
  </>
}

/**
 * Make panel resizing work even when an iframe is nearby.
 *
 * This is a somewhat brute-force solution. The issue is that
 * react-resizable-panels does not expose drag start/end events.
 * Also, its onPointerUp/onPointerDown and onMouseUp/onMouseDown
 * don't consistently fire (e.g. onMouseDown can fire without
 * a corresponding onMouseUp)
 *
 * This should not break iframe interaction: clicks inside an iframe
 * still ignore this listener. It is only enabled in edit or debug mode
 * as a safeguard. If this becomes an issue, we may need to detect proximity
 * to the resize handle instead.
 */
function useWorkaroundToAllowResizingWithNearbyIframes(isEditOrDebugMode: boolean) {
    useEffect(() => {
      if (!isEditOrDebugMode) return

      const abortController = new AbortController()

      document.addEventListener("pointerdown", () => {
          voteToDisableIframePointerEvents('viewer', 'disable')
      }, { signal: abortController.signal, capture: true })

      document.addEventListener("pointerup", () => {
        voteToDisableIframePointerEvents('viewer', 'enable')
      }, { signal: abortController.signal, capture: true })

      return () => abortController.abort()
    }, [ isEditOrDebugMode ])
}
